import dayjs from "dayjs";
import { useEffect, useRef, useState } from "react";
import { useDispatch, useSelector } from "react-redux";
import { useNavigate, useSearchParams } from "react-router-dom";
import {
  Bar,
  BarChart,
  CartesianGrid,
  Legend,
  ResponsiveContainer,
  Tooltip,
  XAxis,
  YAxis,
} from "recharts";
import PeopleIcon from "@mui/icons-material/People";
import PersonAddIcon from "@mui/icons-material/PersonAdd";
import VisibilityIcon from "@mui/icons-material/Visibility";
import LoyaltyIcon from "@mui/icons-material/Loyalty";
import OpenInNewIcon from "@mui/icons-material/OpenInNew";
import CampaignIcon from "@mui/icons-material/Campaign";
import ReceiptLongIcon from "@mui/icons-material/ReceiptLong";
import { useCompany } from "../../components/context/CompanyContext";
import { canAccessPath } from "../../util/accessControl";
import CustomGrid from "../../components/grid/CustomGrid";
import Loading from "../../components/loading/loading";
import { titleSetRef } from "../../redux/titleRedux";
import { userRequest } from "../../requestMethods";
import { formatPricing, retunrMonth } from "../../util/helper";
import { effectiveStories } from "../../util/storeScope";
import "../../App.css";

const BRAND = "#3B82F6";

function NavTab({ label, description, icon, active, onClick }) {
  return (
    <div
      onClick={onClick}
      style={{
        background: active ? `linear-gradient(135deg, ${BRAND}, #1D4ED8)` : "#fff",
        border: `2px solid ${active ? BRAND : "#E5E7EB"}`,
        borderRadius: 14,
        padding: "12px 20px",
        cursor: "pointer",
        transition: "all 0.18s",
        display: "flex",
        alignItems: "center",
        gap: 12,
        minWidth: 190,
        boxShadow: active ? "0 4px 16px rgba(59,130,246,0.25)" : "0 1px 4px rgba(0,0,0,0.06)",
        userSelect: "none",
      }}
    >
      <div style={{
        width: 36, height: 36, borderRadius: 10,
        background: active ? "rgba(255,255,255,0.20)" : "#F3F4F6",
        display: "flex", alignItems: "center", justifyContent: "center", flexShrink: 0,
      }}>
        {icon}
      </div>
      <div>
        <div style={{ fontSize: 13, fontWeight: 700, color: active ? "#fff" : "#111827", fontFamily: "Manrope" }}>{label}</div>
        <div style={{ fontSize: 11, color: active ? "rgba(255,255,255,0.75)" : "#9CA3AF", fontFamily: "Manrope" }}>{description}</div>
      </div>
    </div>
  );
}

function KpiCard({ icon, title, heroValue, sub, gradient, onClick, cta }) {
  const [hover, setHover] = useState(false);
  return (
    <div
      onClick={onClick}
      onMouseEnter={() => onClick && setHover(true)}
      onMouseLeave={() => setHover(false)}
      style={{
        background: `linear-gradient(135deg, ${gradient[0]}, ${gradient[1]})`,
        borderRadius: 16,
        overflow: "hidden",
        cursor: onClick ? "pointer" : "default",
        transition: "transform 0.18s, box-shadow 0.18s",
        transform: hover ? "translateY(-3px)" : "none",
        boxShadow: hover ? "0 10px 28px rgba(0,0,0,0.20)" : "0 3px 10px rgba(0,0,0,0.10)",
        position: "relative",
      }}
    >
      <div style={{ position: "absolute", right: -24, top: -24, width: 110, height: 110, borderRadius: "50%", background: "rgba(255,255,255,0.10)", pointerEvents: "none" }} />
      <div style={{ position: "absolute", right: 16, bottom: -32, width: 80, height: 80, borderRadius: "50%", background: "rgba(255,255,255,0.06)", pointerEvents: "none" }} />

      <div style={{ padding: "18px 20px 14px", position: "relative" }}>
        <div style={{ display: "flex", justifyContent: "space-between", alignItems: "flex-start", marginBottom: 14 }}>
          <span style={{ fontSize: 10, fontWeight: 800, color: "rgba(255,255,255,0.80)", textTransform: "uppercase", letterSpacing: "0.09em", fontFamily: "Manrope" }}>{title}</span>
          {icon && (
            <div style={{ width: 34, height: 34, borderRadius: 10, background: "rgba(255,255,255,0.20)", display: "flex", alignItems: "center", justifyContent: "center", flexShrink: 0 }}>
              {icon}
            </div>
          )}
        </div>
        <div style={{ fontSize: 26, fontWeight: 900, color: "#fff", fontFamily: "Manrope", letterSpacing: "-0.02em", lineHeight: 1.1 }}>{heroValue}</div>
        <div style={{ fontSize: 11, color: "rgba(255,255,255,0.70)", fontFamily: "Manrope", marginTop: 5 }}>{sub}</div>
      </div>

      <div style={{ borderTop: "1px solid rgba(255,255,255,0.15)", padding: "9px 20px", display: "flex", alignItems: "center", justifyContent: "space-between", background: "rgba(0,0,0,0.10)" }}>
        <span style={{ fontSize: 11, color: "rgba(255,255,255,0.85)", fontFamily: "Manrope", fontWeight: 600 }}>{cta || "Ver detalhes"}</span>
        <span style={{ fontSize: 14, color: "rgba(255,255,255,0.65)" }}>›</span>
      </div>
    </div>
  );
}

function CountCard({ label, count, hint, color, onClick }) {
  const [hover, setHover] = useState(false);
  const c = color || { bg: "#F3F4F6", color: "#374151" };
  return (
    <div
      onClick={onClick}
      onMouseEnter={() => onClick && setHover(true)}
      onMouseLeave={() => setHover(false)}
      style={{
        background: c.bg,
        border: `1.5px solid ${c.color}33`,
        borderRadius: 12,
        padding: "14px 16px",
        cursor: onClick ? "pointer" : "default",
        transition: "transform 0.15s, box-shadow 0.15s",
        transform: hover ? "translateY(-2px)" : "none",
        boxShadow: hover ? "0 4px 12px rgba(0,0,0,0.12)" : "0 1px 4px rgba(0,0,0,0.06)",
      }}
    >
      <div style={{ display: "flex", justifyContent: "space-between", alignItems: "center", marginBottom: 6 }}>
        <span style={{ fontSize: 12, fontWeight: 700, color: c.color, fontFamily: "Manrope" }}>{label}</span>
        {onClick && <OpenInNewIcon style={{ fontSize: 13, color: c.color, opacity: 0.6 }} />}
      </div>
      <div style={{ fontSize: 22, fontWeight: 900, color: c.color, fontFamily: "Manrope" }}>{count}</div>
      {hint && <div style={{ fontSize: 11, color: c.color, opacity: 0.7, fontFamily: "Manrope", marginTop: 2 }}>{hint}</div>}
    </div>
  );
}

function BucketCard({ label, count, hint, onClick }) {
  const [hover, setHover] = useState(false);
  return (
    <div
      onClick={onClick}
      onMouseEnter={() => onClick && setHover(true)}
      onMouseLeave={() => setHover(false)}
      style={{
        background: "#fff",
        border: "1px solid #E5E7EB",
        borderRadius: 12,
        padding: "16px 18px",
        textAlign: "center",
        cursor: onClick ? "pointer" : "default",
        transition: "transform 0.15s, box-shadow 0.15s",
        transform: hover ? "translateY(-2px)" : "none",
        boxShadow: hover ? "0 4px 12px rgba(0,0,0,0.10)" : "0 1px 4px rgba(0,0,0,0.05)",
      }}
    >
      <div style={{ display: "flex", justifyContent: "center", alignItems: "center", gap: 4 }}>
        <span style={{ fontSize: 11, fontWeight: 700, color: "#6B7280", fontFamily: "Manrope", textTransform: "uppercase", letterSpacing: "0.06em" }}>{label}</span>
        {onClick && <OpenInNewIcon style={{ fontSize: 12, color: "#9CA3AF" }} />}
      </div>
      <div style={{ fontSize: 22, fontWeight: 900, color: "#111827", fontFamily: "Manrope", margin: "8px 0 4px" }}>{count}</div>
      {hint && <div style={{ fontSize: 11, color: "#9CA3AF", fontFamily: "Manrope" }}>{hint}</div>}
    </div>
  );
}

function SectionCard({ title, sub, action, children }) {
  return (
    <div style={{
      background: "#fff",
      borderRadius: 14,
      border: "1px solid #E5E7EB",
      boxShadow: "0 2px 8px rgba(0,0,0,0.06)",
      padding: "18px 20px 16px",
    }}>
      <div style={{ display: "flex", justifyContent: "space-between", alignItems: "center", marginBottom: 14 }}>
        <div>
          <div style={{ fontSize: 14, fontWeight: 700, color: "#111827", fontFamily: "Manrope" }}>{title}</div>
          {sub && <div style={{ fontSize: 11, color: "#9CA3AF", fontFamily: "Manrope", marginTop: 2 }}>{sub}</div>}
        </div>
        {action}
      </div>
      {children}
    </div>
  );
}

function ChartCard({ title, sub, children }) {
  return (
    <div style={{
      background: "#fff",
      borderRadius: 14,
      border: "1px solid #E5E7EB",
      boxShadow: "0 2px 8px rgba(0,0,0,0.06)",
      padding: "18px 18px 14px",
    }}>
      <div style={{ marginBottom: 14 }}>
        <div style={{ fontSize: 13, fontWeight: 700, color: "#111827", fontFamily: "Manrope" }}>{title}</div>
        {sub && <div style={{ fontSize: 11, color: "#9CA3AF", fontFamily: "Manrope", marginTop: 2 }}>{sub}</div>}
      </div>
      {children}
    </div>
  );
}

const gridColumns = [
  { field: "name", type: "text", headerName: "Nome", width: 220, align: "left" },
  { field: "contact", type: "text", headerName: "Contato", width: 180, align: "left" },
  { field: "quantity", type: "number", headerName: "Qtde de compras", width: 150, align: "left" },
  {
    field: "amount",
    type: "number",
    headerName: "Total",
    width: 160,
    align: "left",
    format: "BRL",
    valueFormatter: (value) => (value === null || value === undefined ? "" : `${formatPricing(value)}`),
  },
  { field: "managedBy", type: "text", headerName: "Vendedor(a)", width: 180, align: "left" },
];

const cashbackColumns = [
  { field: "name", type: "text", headerName: "Nome", width: 220, align: "left" },
  { field: "contact", type: "text", headerName: "Contato", width: 180, align: "left" },
  {
    field: "amount",
    type: "number",
    headerName: "Total",
    width: 160,
    align: "left",
    format: "BRL",
    valueFormatter: (value) => (value === null || value === undefined ? "" : `${formatPricing(value)}`),
  },
  {
    field: "expiryDate",
    headerName: "Data Vencimento",
    width: 170,
    align: "center",
    headerAlign: "center",
    type: "date",
    format: "dd/MM/yyyy",
    valueGetter: (value) => value && new Date(value),
  },
  { field: "managedName", type: "text", headerName: "Vendedor(a)", width: 180, align: "left" },
];

const columnsAnalitico = [
  { field: "name", headerName: "Nome", width: 200 },
  { field: "contact", headerName: "Contato", width: 160 },
  { field: "cnpj_cpf", headerName: "CNPJ", width: 150 },
  { field: "email", headerName: "Email", width: 200 },
  { field: "createdAt", headerName: "Criado", width: 120, align: "center", headerAlign: "center", format: "dd/MM/yyyy" },
  { field: "managedBy", headerName: "Vendedor(a)", width: 180 },
  { field: "utmSource", headerName: "UTM Source", width: 120 },
  { field: "utmCampaign", headerName: "UTM Campaign", width: 150 },
  { field: "utmMedium", headerName: "UTM Medium", width: 120 },
];

export default function IndicadoresClientes() {
  const [searchParams] = useSearchParams();
  // Veio do menu lateral "ADS": abre direto na aba ADS Performance e esconde os
  // cards "Cliente" e "Pedidos Clientes".
  const fromAds = searchParams.get("ads") === "1";
  const [activeTab, setActiveTab] = useState(fromAds ? "ads" : "cliente");

  // --- Cliente state ---
  const [status, setStatus] = useState(null);
  const [register, setRegister] = useState(null);
  const [acess, setAcess] = useState(null);
  const [share, setShare] = useState([]);
  const [month, setMonth] = useState([]);
  const [byRep, setByRep] = useState([]);
  const [reps, setReps] = useState([]);
  const [topCustomers, setTopCustomers] = useState([]);
  const [fidelizacao, setFidelizacao] = useState([]);
  const [cashback, setCashback] = useState([]);
  const [premium, setPremium] = useState([]);
  const [stories, setStories] = useState([]);
  const [selected, setSelected] = useState([]);
  const [storeIds, setStoreIds] = useState([]);

  // --- ADS state ---
  const [utmBreakdown, setUtmBreakdown] = useState(null);
  const [trafficAds, setTrafficAds] = useState(null);
  const [analitico, setAnalitico] = useState([]);
  const analiticoRef = useRef(null);

  // --- Shared filter state ---
  const [dataIni, setDateIni] = useState(() => dayjs().add(-7, "day").format("YYYY-MM-DD"));
  const [dataFim, setDateFim] = useState(() => dayjs().format("YYYY-MM-DD"));
  const [readonly, setReadonly] = useState("7");
  const [isLoading, setIsLoading] = useState(true);

  const currentUser = useSelector((state) => state.user.currentUser);
  const { companyId, slug, midiaReport, accessControl } = useCompany();
  const nav = useNavigate();
  const dispatch = useDispatch();

  const urlMidia = midiaReport ? `https://app.reportei.com/embed/${midiaReport}` : null;

  useEffect(() => {
    dispatch(titleSetRef({ title: "Indicadores Clientes" }));
    if (!currentUser) nav("../login");
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  // Reage ao parâmetro ?ads=1 mesmo quando já se está na tela (rota não remonta,
  // só muda a query): força a aba ADS Performance.
  useEffect(() => {
    if (searchParams.get("ads") === "1") setActiveTab("ads");
  }, [searchParams]);

  useEffect(() => {
    if (companyId > 0 && currentUser) {
      getStories();
      getUserRep();
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [companyId]);

  useEffect(() => {
    setStoreIds(selected.map((s) => s.value));
  }, [selected]);

  useEffect(() => {
    if (companyId > 0 && currentUser) {
      setIsLoading(true);
      carregarTudo();
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [companyId, dataIni, dataFim, storeIds]);

  const buildObj = () => ({
    start: dataIni,
    end: dataFim,
    stories: effectiveStories(currentUser, storeIds),
  });

  async function carregarTudo() {
    try {
      await Promise.all([
        getStatus(),
        getRegister(),
        getAcess(),
        getShare(),
        getMonth(),
        getByRep(),
        getTopCustomers(),
        getFidelizacao(),
        getCashback(),
        getPremium(),
        getUtmBreakdown(),
        getTrafficAds(),
      ]);
    } finally {
      setIsLoading(false);
    }
  }

  const getStories = async () => {
    try {
      const res = await userRequest.get(`${companyId}/store/stories`);
      const list = res.data.map((item) => ({ label: item.store, value: item.storeId }));
      setStories(list);
      if (currentUser?.position === "Gerente") {
        const userStore = list.find((s) => s.value === currentUser.storeId);
        if (userStore) setSelected([userStore]);
      }
    } catch {}
  };

  const getUserRep = async () => {
    try {
      const res = await userRequest.get(`${companyId}/usersRep`);
      setReps(res.data || []);
    } catch {}
  };

  const getStatus = async () => {
    try {
      const eff = effectiveStories(currentUser, storeIds);
      const qs = eff.length > 0 ? `?stories=${eff.join(",")}` : "";
      const res = await userRequest.get(`${companyId}/reportCustomer/customersStatus${qs}`);
      setStatus(res.data);
    } catch {}
  };

  const getRegister = async () => {
    try {
      const res = await userRequest.post(`${companyId}/reportCustomer/customersRegister`, buildObj());
      setRegister(res.data);
    } catch {}
  };

  const getAcess = async () => {
    try {
      const res = await userRequest.post(`${companyId}/reportCustomer/customersAcess`, buildObj());
      setAcess(res.data);
    } catch {}
  };

  const getShare = async () => {
    try {
      const res = await userRequest.post(`${companyId}/reportCustomer/share`, buildObj());
      setShare(Array.isArray(res.data) ? res.data : []);
    } catch {}
  };

  const getMonth = async () => {
    try {
      const res = await userRequest.get(`${companyId}/reportCustomer/customersmonth`);
      setMonth(res.data.map((item) => ({ criado: retunrMonth(item.createdAt), count: item.count })));
    } catch {}
  };

  const getByRep = async () => {
    try {
      const res = await userRequest.post(`${companyId}/reportCustomer/customersRep`, buildObj());
      setByRep(Array.isArray(res.data) ? res.data : []);
    } catch {}
  };

  const getTopCustomers = async () => {
    try {
      const res = await userRequest.post(`${companyId}/reportCustomer/topCustomers`, { ...buildObj(), limit: 20 });
      setTopCustomers(res.data || []);
    } catch {}
  };

  const getFidelizacao = async () => {
    try {
      const res = await userRequest.post(`${companyId}/reportCustomer/topCustomers`, { ...buildObj(), fidelizacao: true });
      setFidelizacao(res.data || []);
    } catch {}
  };

  const getCashback = async () => {
    try {
      const res = await userRequest.get(`${companyId}/cashback/user/0`);
      setCashback(res.data || []);
    } catch {}
  };

  const getPremium = async () => {
    try {
      const res = await userRequest.get(`${companyId}/customers/premium`);
      setPremium(res.data || []);
    } catch {}
  };

  const getUtmBreakdown = async () => {
    try {
      const res = await userRequest.post(`${companyId}/reportCustomer/utmBreakdown`, { start: dataIni, end: dataFim });
      setUtmBreakdown(res.data);
    } catch {}
  };

  const getTrafficAds = async () => {
    try {
      const res = await userRequest.post(`${companyId}/reportCustomer/customersAcess`, { start: dataIni, end: dataFim });
      setTrafficAds(res.data);
    } catch {}
  };

  const fetchAnalitico = async (typeAcess) => {
    try {
      const res = await userRequest.post(`${companyId}/reportCustomer/customersAnaliticoAcess`, { start: dataIni, end: dataFim, typeAcess });
      setAnalitico(res.data);
      setTimeout(() => analiticoRef.current?.scrollIntoView({ behavior: "smooth" }), 100);
    } catch {}
  };

  const handleChange = (e) => {
    const v = e.target.value;
    setReadonly(v);
    switch (v) {
      case "0": setDateIni(dayjs().format("YYYY-MM-DD")); setDateFim(dayjs().format("YYYY-MM-DD")); break;
      case "7": setDateIni(dayjs().add(-7, "day").format("YYYY-MM-DD")); setDateFim(dayjs().format("YYYY-MM-DD")); break;
      case "15": setDateIni(dayjs().add(-15, "day").format("YYYY-MM-DD")); setDateFim(dayjs().format("YYYY-MM-DD")); break;
      case "30": setDateIni(dayjs().add(-30, "day").format("YYYY-MM-DD")); setDateFim(dayjs().format("YYYY-MM-DD")); break;
      default: break;
    }
  };

  const periodLabel =
    readonly === "F" ? "filtro personalizado" : readonly === "0" ? "hoje" : `últimos ${readonly} dias`;

  const hasUtmData = utmBreakdown && utmBreakdown.byCampaign?.length > 0;

  // Loja selecionada → carrega para a customerList (filtra pela loja do vendedor)
  const storeQSIds = effectiveStories(currentUser, storeIds);
  const storeQS = storeQSIds.length > 0 ? `&storeId=${storeQSIds.join(",")}` : "";

  const statusItems = [
    { label: "Total", count: status?.qtdeCustomer || 0, hint: "clientes aprovados", color: { bg: "#c2e0f4", color: "#0e67a3" }, to: `/${slug}/customers?enabled=Aprovado&from=indicadoresclientes${storeQS}` },
    { label: "Ativos", count: status?.ativos || 0, hint: "últimos 90 dias", color: { bg: "#c7f6da", color: "#06632b" }, to: `/${slug}/customers?activeLast90=true&enabled=Aprovado&from=indicadoresclientes${storeQS}` },
    { label: "Inativos", count: status?.inativos || 0, hint: "compras de 91 a 180 dias", color: { bg: "#f1d1c3", color: "#863c1c" }, to: `/${slug}/customers?inativos=true&enabled=Aprovado&from=indicadoresclientes${storeQS}` },
    { label: "Reconquista", count: status?.reconquista || 0, hint: "compras há mais de 180 dias", color: { bg: "#f7e0f3", color: "#a3298f" }, to: `/${slug}/customers?reconquista=true&enabled=Aprovado&from=indicadoresclientes${storeQS}` },
  ];

  return (
    <div style={{ padding: "16px 16px 48px 4px", fontFamily: "Manrope", background: "#F9FAFB", minHeight: "100vh" }}>

      {/* Nav tabs — antes do filtro */}
      <div style={{ display: "flex", gap: 12, marginBottom: 16 }}>
        {!fromAds && (
          <NavTab
            label="Cliente"
            description="Indicadores de clientes"
            icon={<PeopleIcon style={{ fontSize: 18, color: activeTab === "cliente" ? "#fff" : "#6B7280" }} />}
            active={activeTab === "cliente"}
            onClick={() => setActiveTab("cliente")}
          />
        )}
        <NavTab
          label="ADS Performance"
          description="Tráfego pago e UTM"
          icon={<CampaignIcon style={{ fontSize: 18, color: activeTab === "ads" ? "#fff" : "#6B7280" }} />}
          active={activeTab === "ads"}
          onClick={() => setActiveTab("ads")}
        />
        {!fromAds &&
          canAccessPath(accessControl, currentUser?.position, `/${slug}/relatorio-pedidos-clientes`) && (
          <NavTab
            label="Pedidos Clientes"
            description="Relatório de pedidos por cliente"
            icon={<ReceiptLongIcon style={{ fontSize: 18, color: "#6B7280" }} />}
            active={false}
            onClick={() => nav(`/${slug}/relatorio-pedidos-clientes`)}
          />
        )}
      </div>

      {/* Header + filtro */}
      <div style={{ display: "flex", alignItems: "center", gap: 12, marginBottom: 20, justifyContent: "space-between" }}>
        <div>
          <span style={{ fontSize: 18, fontWeight: 700, color: "#111827" }}>
            {activeTab === "cliente" ? "Indicadores Clientes" : "ADS Performance"}
          </span>
          <span style={{ fontSize: 12, color: "#9CA3AF", marginLeft: 8 }}>{periodLabel}</span>
        </div>
        <div style={{ display: "flex", alignItems: "flex-end", gap: 12 }}>

          {/* Filtro Lojas — apenas na aba Cliente */}
          {activeTab === "cliente" && stories.length > 0 && (() => {
            const isGerente = currentUser?.position === "Gerente";
            return (
              <div>
                <div style={{ fontSize: 10, fontWeight: 700, color: "#9CA3AF", textTransform: "uppercase", letterSpacing: "0.07em", marginBottom: 4 }}>Lojas</div>
                <div style={{ display: "flex", gap: 6, flexWrap: "wrap", border: "1px solid #E5E7EB", borderRadius: 8, padding: "6px 8px", background: "#fff" }}>
                  <label
                    style={{ cursor: isGerente ? "default" : "pointer", padding: "3px 10px", borderRadius: 6, border: `1px solid ${selected.length === 0 ? BRAND : "#E5E7EB"}`, background: selected.length === 0 ? BRAND : "transparent", color: selected.length === 0 ? "#fff" : "#6B7280", fontSize: 12, fontWeight: 600 }}
                    onClick={() => !isGerente && setSelected([])}
                  >
                    Todas
                  </label>
                  {stories.map((story) => {
                    const isActive = selected.some((s) => s.value === story.value);
                    return (
                      <label
                        key={story.value}
                        style={{ cursor: isGerente ? "default" : "pointer", padding: "3px 10px", borderRadius: 6, border: `1px solid ${isActive ? BRAND : "#E5E7EB"}`, background: isActive ? BRAND : "transparent", color: isActive ? "#fff" : "#6B7280", fontSize: 12, fontWeight: 600, opacity: isGerente ? 0.7 : 1 }}
                        onClick={() => {
                          if (isGerente) return;
                          setSelected(isActive ? selected.filter((s) => s.value !== story.value) : [...selected, story]);
                        }}
                      >
                        {story.label}
                      </label>
                    );
                  })}
                </div>
              </div>
            );
          })()}

          <div>
            <div style={{ fontSize: 10, fontWeight: 700, color: "#9CA3AF", textTransform: "uppercase", letterSpacing: "0.07em", marginBottom: 4 }}>Período</div>
            <div style={{ display: "flex", gap: 6, flexWrap: "wrap", border: "1px solid #E5E7EB", borderRadius: 8, padding: "6px 8px", background: "#fff" }}>
              {[
                { v: "30", l: "30 dias" },
                { v: "15", l: "15 dias" },
                { v: "7", l: "7 dias" },
                { v: "0", l: "Hoje" },
                { v: "F", l: "Filtro" },
              ].map(({ v, l }) => (
                <label
                  key={v}
                  style={{
                    cursor: "pointer",
                    padding: "3px 10px",
                    borderRadius: 6,
                    border: `1px solid ${readonly === v ? BRAND : "#E5E7EB"}`,
                    background: readonly === v ? BRAND : "transparent",
                    color: readonly === v ? "#fff" : "#6B7280",
                    fontSize: 12,
                    fontWeight: 600,
                  }}
                >
                  <input type="radio" value={v} checked={readonly === v} onChange={handleChange} style={{ display: "none" }} />
                  {l}
                </label>
              ))}
              {readonly === "F" && (
                <>
                  <div style={{ width: 1, background: "#E5E7EB", margin: "2px 2px" }} />
                  <input type="date" value={dataIni} onChange={(e) => setDateIni(e.target.value)} min="2023-01-01" style={{ fontSize: 12, border: "none", outline: "none" }} />
                  <span style={{ color: "#9CA3AF", fontSize: 12 }}>—</span>
                  <input type="date" value={dataFim} onChange={(e) => setDateFim(e.target.value)} min="2023-01-01" style={{ fontSize: 12, border: "none", outline: "none" }} />
                </>
              )}
            </div>
          </div>
        </div>
      </div>

      {isLoading ? (
        <Loading />
      ) : activeTab === "cliente" ? (
        <div style={{ display: "flex", flexDirection: "column", gap: 20 }}>

          {/* KPIs hero — linha 1: métricas de base */}
          <div style={{ display: "grid", gridTemplateColumns: "repeat(4, 1fr)", gap: 16 }}>
            <KpiCard
              icon={<PeopleIcon style={{ fontSize: 20, color: "#fff" }} />}
              title="Total de Clientes"
              heroValue={status?.qtdeCustomer || 0}
              sub="clientes aprovados"
              gradient={["#3B82F6", "#1D4ED8"]}
              cta="Ver clientes"
              onClick={() => nav(`/${slug}/customers?enabled=Aprovado${storeQS}&from=indicadoresclientes`)}
            />
            <KpiCard
              icon={<PersonAddIcon style={{ fontSize: 20, color: "#fff" }} />}
              title="Novos Cadastros"
              heroValue={register?.aprovado || 0}
              sub={`${(register?.pendente || 0)} pendentes no período`}
              gradient={["#10B981", "#059669"]}
              cta="Ver novos clientes"
              onClick={() => nav(`/${slug}/customers?start=${dataIni}&end=${dataFim}&enabled=Aprovado&from=indicadoresclientes`)}
            />
            <KpiCard
              icon={<VisibilityIcon style={{ fontSize: 20, color: "#fff" }} />}
              title="Acessos ao Catálogo"
              heroValue={acess?.totalAcess || 0}
              sub={`${acess?.acess || 0} compraram`}
              gradient={["#EE8253", "#E8502A"]}
              cta="Ver acessos"
              onClick={() => nav(`/${slug}/customers?acessStart=${dataIni}&acessEnd=${dataFim}&enabled=Aprovado&from=indicadoresclientes`)}
            />
            <KpiCard
              icon={<LoyaltyIcon style={{ fontSize: 20, color: "#fff" }} />}
              title="Clientes Ativos"
              heroValue={status?.ativos || 0}
              sub="compraram nos últimos 90 dias"
              gradient={["#8B5CF6", "#6D28D9"]}
              cta="Ver ativos"
              onClick={() => nav(`/${slug}/customers?activeLast90=true&enabled=Aprovado${storeQS}&from=indicadoresclientes`)}
            />
          </div>

          {/* KPIs hero — linha 2: análises de clientes */}
          <div style={{ display: "grid", gridTemplateColumns: "repeat(4, 1fr)", gap: 16 }}>
            <KpiCard
              title="Top 20 Clientes"
              heroValue={formatPricing(
                topCustomers.length > 0
                  ? topCustomers.reduce((s, c) => s + (c.amount || 0), 0) / topCustomers.length
                  : 0
              )}
              sub={`média de compras · ${topCustomers.length} clientes`}
              gradient={["#F59E0B", "#D97706"]}
              cta="Ver top clientes"
              onClick={() => {
                const ids = topCustomers.map((c) => c.customerId).join(",");
                nav(`/${slug}/customers?customerIds=${ids}&from=indicadoresclientes`);
              }}
            />
            <KpiCard
              title="Fidelização de Clientes"
              heroValue={formatPricing(
                fidelizacao.length > 0
                  ? fidelizacao.reduce((s, c) => s + (c.amount || 0), 0) / fidelizacao.length
                  : 0
              )}
              sub={`média de compras · ${fidelizacao.length} clientes`}
              gradient={["#EC4899", "#BE185D"]}
              cta="Ver clientes para fidelizar"
              onClick={() => {
                const ids = fidelizacao.map((c) => c.customerId).join(",");
                nav(`/${slug}/customers?customerIds=${ids}&from=indicadoresclientes`);
              }}
            />
            <KpiCard
              title="Cashback de Clientes"
              heroValue={formatPricing(
                cashback.reduce((s, c) => s + (Number(c.amount) || 0), 0)
              )}
              sub={`${cashback.length} cliente${cashback.length !== 1 ? "s" : ""} com saldo ativo`}
              gradient={["#14B8A6", "#0F766E"]}
              cta="Ver clientes com cashback"
              onClick={() => {
                const ids = cashback.map((c) => c.customerId).filter(Boolean).join(",");
                if (ids) nav(`/${slug}/customers?customerIds=${ids}&from=indicadoresclientes`);
              }}
            />
            <KpiCard
              title="Clientes Premium"
              heroValue={premium.length}
              sub={`${premium.filter((c) => c.orderAmount > 0).length} com pedido · ${premium.filter((c) => c.cartTotal > 0).length} com carrinho`}
              gradient={["#7C3AED", "#4C1D95"]}
              cta="Ver clientes premium"
              onClick={() => {
                const ids = premium.map((c) => c.customerId).filter(Boolean).join(",");
                if (ids) nav(`/${slug}/customers?customerIds=${ids}&from=indicadoresclientes`);
              }}
            />
          </div>

          {/* Status do cliente */}
          <SectionCard
            title="Status do Cliente"
            sub="base total de clientes aprovados"
            action={
              <span
                onClick={() => nav(`/${slug}/customers?enabled=Aprovado${storeQS}&from=indicadoresclientes`)}
                style={{ fontSize: 12, color: BRAND, fontWeight: 600, cursor: "pointer", display: "flex", alignItems: "center", gap: 4 }}
              >
                Ver todos <OpenInNewIcon style={{ fontSize: 13 }} />
              </span>
            }
          >
            <div style={{ display: "grid", gridTemplateColumns: "repeat(4, 1fr)", gap: 12 }}>
              {statusItems.map((item) => (
                <CountCard key={item.label} label={item.label} count={item.count} hint={item.hint} color={item.color} onClick={() => nav(item.to)} />
              ))}
            </div>
          </SectionCard>

          {/* Novos cadastros + Acesso ao catálogo */}
          <div style={{ display: "grid", gridTemplateColumns: "1fr 1fr", gap: 16 }}>

            {/* Novos Cadastros */}
            <div style={{ background: "#fff", borderRadius: 14, border: "1px solid #E5E7EB", boxShadow: "0 2px 8px rgba(0,0,0,0.06)", padding: "18px 20px 16px" }}>
              <div style={{ display: "flex", justifyContent: "space-between", alignItems: "center", marginBottom: 14 }}>
                <span style={{ fontSize: 14, fontWeight: 700, color: "#111827", fontFamily: "Manrope" }}>Novos Cadastros</span>
                <span style={{ fontSize: 11, color: "#9CA3AF", fontFamily: "Manrope" }}>{periodLabel}</span>
              </div>
              <div style={{ display: "grid", gridTemplateColumns: "repeat(4, 1fr)", gap: 12 }}>
                <BucketCard label="Aprovados" count={register?.aprovado || 0} onClick={() => nav(`/${slug}/customers?start=${dataIni}&end=${dataFim}&enabled=Aprovado&from=indicadoresclientes`)} />
                <BucketCard label="Pendentes" count={register?.pendente || 0} onClick={() => nav(`/${slug}/customers?start=${dataIni}&end=${dataFim}&enabled=Pendente&from=indicadoresclientes`)} />
                <BucketCard label="Recusados" count={register?.negado || 0} onClick={() => nav(`/${slug}/customers?start=${dataIni}&end=${dataFim}&enabled=Negado&from=indicadoresclientes`)} />
                <BucketCard label="Leads" count={register?.lead || 0} onClick={() => nav(`/${slug}/leads`)} />
              </div>
            </div>

            {/* Acesso ao Catálogo */}
            <div style={{ background: "#fff", borderRadius: 14, border: "1px solid #E5E7EB", boxShadow: "0 2px 8px rgba(0,0,0,0.06)", padding: "18px 20px 16px" }}>
              <div style={{ display: "flex", justifyContent: "space-between", alignItems: "center", marginBottom: 14 }}>
                <span style={{ fontSize: 14, fontWeight: 700, color: "#111827", fontFamily: "Manrope" }}>Acesso ao Catálogo</span>
                <span
                  onClick={() => nav(`/${slug}/customers?acessStart=${dataIni}&acessEnd=${dataFim}&enabled=Aprovado&from=indicadoresclientes`)}
                  style={{ fontSize: 12, color: BRAND, fontWeight: 600, cursor: "pointer", display: "flex", alignItems: "center", gap: 4 }}
                >
                  Ver acessos <OpenInNewIcon style={{ fontSize: 13 }} />
                </span>
              </div>
              <div style={{ display: "grid", gridTemplateColumns: "repeat(4, 1fr)", gap: 12 }}>
                <BucketCard label="Acessos" count={acess?.totalAcess || 0} onClick={() => nav(`/${slug}/customers?acessType=acessou&acessStart=${dataIni}&acessEnd=${dataFim}&enabled=Aprovado&from=indicadoresclientes${storeQS}`)} />
                <BucketCard label="Comprou" count={acess?.acess || 0} onClick={() => nav(`/${slug}/customers?acessType=comprou&acessStart=${dataIni}&acessEnd=${dataFim}&enabled=Aprovado&from=indicadoresclientes${storeQS}`)} />
                <BucketCard label="Não comprou" count={acess?.nAcess || 0} onClick={() => nav(`/${slug}/customers?acessType=naoComprou&acessStart=${dataIni}&acessEnd=${dataFim}&enabled=Aprovado&from=indicadoresclientes${storeQS}`)} />
                <BucketCard label="Não acessou" count={acess?.cNotAcess || 0} onClick={() => nav(`/${slug}/customers?acessType=naoAcessou&acessStart=${dataIni}&acessEnd=${dataFim}&enabled=Aprovado&from=indicadoresclientes${storeQS}`)} />
              </div>
            </div>

          </div>

          {/* Gráficos: histórico mensal + compartilhamento */}
          <div style={{ display: "grid", gridTemplateColumns: "1fr 1fr", gap: 16 }}>
            <ChartCard title="Histórico de Cadastro" sub="do primeiro dia do ano até hoje">
              <ResponsiveContainer width="100%" height={250}>
                <BarChart data={month}>
                  <CartesianGrid strokeDasharray="3 3" />
                  <XAxis dataKey="criado" interval={0} angle={-40} tick={{ fontSize: 11 }} />
                  <YAxis tick={{ fontSize: 11 }} />
                  <Tooltip />
                  <Bar dataKey="count" name="Cadastros" fill={BRAND} barSize={12} radius={[4, 4, 0, 0]} />
                </BarChart>
              </ResponsiveContainer>
            </ChartCard>
            <ChartCard title="Compartilhamento do Catálogo" sub={periodLabel}>
              <ResponsiveContainer width="100%" height={250}>
                <BarChart data={share}>
                  <CartesianGrid strokeDasharray="3 3" />
                  <XAxis dataKey="name" interval={0} angle={-40} tick={{ fontSize: 11 }} />
                  <YAxis tick={{ fontSize: 11 }} />
                  <Tooltip />
                  <Legend />
                  <Bar dataKey="lido" name="Acessado" stackId="a" fill={BRAND} barSize={8} />
                  <Bar dataKey="dif" name="Não acessado" stackId="a" fill="#999999" barSize={8} />
                </BarChart>
              </ResponsiveContainer>
            </ChartCard>
          </div>

          {/* Cadastro por representante (condicional) */}
          {reps.length > 0 && byRep.length > 0 && (
            <ChartCard title="Cadastro por Representante" sub={periodLabel}>
              <ResponsiveContainer width="100%" height={250}>
                <BarChart data={byRep}>
                  <CartesianGrid strokeDasharray="3 3" />
                  <XAxis dataKey="userRepName" interval={0} angle={-40} tick={{ fontSize: 11 }} />
                  <YAxis tick={{ fontSize: 11 }} />
                  <Tooltip />
                  <Bar dataKey="total" name="Cadastros" fill={BRAND} barSize={12} radius={[4, 4, 0, 0]} />
                </BarChart>
              </ResponsiveContainer>
            </ChartCard>
          )}

        </div>
      ) : (
        /* ===== ADS Performance ===== */
        <div style={{ display: "flex", flexDirection: "column", gap: 20 }}>

          {/* KPIs tráfego pago */}
          <div style={{ display: "grid", gridTemplateColumns: "repeat(4, 1fr)", gap: 16 }}>
            <KpiCard
              icon={<PersonAddIcon style={{ fontSize: 20, color: "#fff" }} />}
              title="Leads (anônimos)"
              heroValue={trafficAds?.trafficLead ?? 0}
              sub="visitantes sem cadastro"
              gradient={["#F59E0B", "#D97706"]}
              cta="Tráfego pago"
            />
            <KpiCard
              icon={<PeopleIcon style={{ fontSize: 20, color: "#fff" }} />}
              title="Total Cadastrados"
              heroValue={trafficAds?.trafficTotal ?? 0}
              sub="via tráfego pago"
              gradient={["#EE8253", "#E8502A"]}
              cta="Ver analítico"
              onClick={() => fetchAnalitico("totalTrafficAcess")}
            />
            <KpiCard
              icon={<VisibilityIcon style={{ fontSize: 20, color: "#fff" }} />}
              title="Aprovados"
              heroValue={trafficAds?.trafficAcess ?? 0}
              sub="clientes aprovados"
              gradient={["#10B981", "#059669"]}
              cta="Ver aprovados"
              onClick={() => fetchAnalitico("trafficAcess")}
            />
            <KpiCard
              icon={<LoyaltyIcon style={{ fontSize: 20, color: "#fff" }} />}
              title="Pendentes"
              heroValue={trafficAds?.trafficPend ?? 0}
              sub="aguardando aprovação"
              gradient={["#8B5CF6", "#6D28D9"]}
              cta="Ver pendentes"
              onClick={() => fetchAnalitico("trafficPend")}
            />
          </div>

          {/* UTM breakdowns */}
          {hasUtmData ? (
            <div style={{ display: "flex", flexDirection: "column", gap: 16 }}>
              {utmBreakdown.byCampaign?.length > 0 && (
                <ChartCard title="Por Campanha (utm_campaign)" sub={periodLabel}>
                  <ResponsiveContainer width="100%" height={220}>
                    <BarChart data={utmBreakdown.byCampaign}>
                      <CartesianGrid strokeDasharray="3 3" />
                      <XAxis dataKey="name" tick={{ fontSize: 11 }} interval={0} angle={-20} />
                      <YAxis tick={{ fontSize: 11 }} allowDecimals={false} />
                      <Tooltip />
                      <Bar dataKey="count" name="Visitas" fill="#5b8dee" barSize={20} radius={[4, 4, 0, 0]} />
                    </BarChart>
                  </ResponsiveContainer>
                </ChartCard>
              )}
            </div>
          ) : (
            utmBreakdown && (
              <div style={{ background: "#fff", borderRadius: 14, border: "1px solid #E5E7EB", padding: 32, textAlign: "center", color: "#9CA3AF", fontFamily: "Manrope", fontSize: 13 }}>
                Nenhum dado de UTM no período selecionado.
              </div>
            )
          )}

          {/* Analítico — clique nos KPIs acima para popular */}
          <div ref={analiticoRef}>
            <SectionCard title="Analítico — Clientes por Tráfego Pago" sub="Clique nos cards acima para filtrar">
              <div style={{ height: 350 }}>
                <CustomGrid
                  gridId="analiticomidialist"
                  rows={analitico}
                  columns={columnsAnalitico}
                  pageSize={45}
                  checkboxSelection={false}
                />
              </div>
            </SectionCard>
          </div>

          {/* Reportei iframe */}
          {urlMidia ? (
            <SectionCard title="Relatório de Mídia" sub="Reportei">
              <iframe src={urlMidia} title="Midia" style={{ width: "100%", height: "100vh", border: "none" }} />
            </SectionCard>
          ) : (
            <div style={{ background: "#fff", borderRadius: 14, border: "1px solid #E5E7EB", padding: 48, textAlign: "center" }}>
              <div style={{ fontSize: 16, fontWeight: 700, color: "#374151", fontFamily: "Manrope" }}>Acesso ao mídia indisponível</div>
              <div style={{ fontSize: 13, color: "#9CA3AF", fontFamily: "Manrope", marginTop: 8 }}>Entre em contato com o administrador para obter acesso.</div>
            </div>
          )}

        </div>
      )}
    </div>
  );
}
